import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { AppButton } from './AppButton';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { ReportDownloadProgress } from '../types/report';

type Props = {
  progress: ReportDownloadProgress | null;
  onCancel?: () => void;
  canCancel?: boolean;
};

function phaseLabel(phase: ReportDownloadProgress['phase']): string {
  switch (phase) {
    case 'preparing':
      return 'Preparing report';
    case 'downloading':
      return 'Downloading';
    case 'saving':
      return 'Saving';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    default:
      return 'Working';
  }
}

export function ReportDownloadProgressView({
  progress,
  onCancel,
  canCancel = false,
}: Props) {
  const palette = useAppColors();

  if (!progress) return null;

  const busy =
    progress.phase === 'preparing' ||
    progress.phase === 'downloading' ||
    progress.phase === 'saving';

  // Only show a percentage when totals are known. Do not fake progress.
  const hasPercent =
    typeof progress.percent === 'number' &&
    Number.isFinite(progress.percent) &&
    progress.totalBytes != null &&
    progress.totalBytes > 0;

  return (
    <View
      style={[
        styles.container,
        {
          borderColor: palette.border,
          backgroundColor: palette.surface,
        },
      ]}
      accessibilityRole="summary"
      accessibilityLabel={`${phaseLabel(progress.phase)}${
        hasPercent ? `, ${Math.round(progress.percent!)} percent` : ''
      }`}
      accessibilityLiveRegion="polite"
    >
      <View style={styles.row}>
        {busy ? <ActivityIndicator color={palette.primary} /> : null}
        <Text style={[styles.title, { color: palette.textPrimary }]}>
          {phaseLabel(progress.phase)}
        </Text>
      </View>
      {hasPercent ? (
        <Text style={[styles.meta, { color: palette.textMuted }]}>
          {Math.round(progress.percent!)}%
        </Text>
      ) : busy ? (
        <Text style={[styles.meta, { color: palette.textMuted }]}>
          Progress size is unknown
        </Text>
      ) : null}
      {progress.errorMessage ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {progress.errorMessage}
        </Text>
      ) : null}
      {busy && canCancel && onCancel ? (
        <AppButton
          title="Cancel download"
          variant="secondary"
          onPress={onCancel}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.sm,
    marginVertical: spacing.md,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  title: { ...typography.body, fontWeight: '600' },
  meta: { ...typography.caption },
  error: { ...typography.caption },
});
